"use client";

import { useEffect, useState } from "react";
import { createClient } from "@/lib/supabase/client";

export type StoredUser = {
  email: string;
  firstName: string;
  lastName: string;
};

const USER_STORAGE_KEY = "vault88-user";

function readLocalStorageUser(): StoredUser | null {
  try {
    const raw = window.localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredUser>;
    if (!parsed.email) return null;
    return {
      email: parsed.email,
      firstName: parsed.firstName ?? "",
      lastName: parsed.lastName ?? "",
    };
  } catch {
    return null;
  }
}

function supabaseUserToStoredUser(
  email: string | undefined,
  fullName: string | undefined
): StoredUser | null {
  if (!email) return null;
  const [firstName = "", ...rest] = (fullName ?? "").trim().split(/\s+/);
  return {
    email,
    firstName,
    lastName: rest.join(" "),
  };
}

/**
 * Returns the "current user" for pre-filling name/email fields. Prefers the
 * signed-in Supabase session, falls back to the anonymous onboarding-modal
 * identity cached in localStorage under `vault88-user`.
 */
export function useStoredUser(): StoredUser | null {
  const [user, setUser] = useState<StoredUser | null>(null);

  useEffect(() => {
    let cancelled = false;
    const supabase = createClient();

    const applyFallback = () => {
      if (cancelled) return;
      setUser(readLocalStorageUser());
    };

    supabase.auth
      .getUser()
      .then(({ data }) => {
        if (cancelled) return;
        const fromSupabase = supabaseUserToStoredUser(
          data.user?.email ?? undefined,
          (data.user?.user_metadata?.full_name as string | undefined) ?? undefined
        );
        if (fromSupabase) {
          setUser(fromSupabase);
        } else {
          applyFallback();
        }
      })
      .catch(applyFallback);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      const fromSupabase = supabaseUserToStoredUser(
        session?.user?.email ?? undefined,
        (session?.user?.user_metadata?.full_name as string | undefined) ?? undefined
      );
      if (fromSupabase) {
        setUser(fromSupabase);
      } else {
        applyFallback();
      }
    });

    const onStorage = (event: StorageEvent) => {
      if (event.key === USER_STORAGE_KEY) applyFallback();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return user;
}
